import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { EntityContacts } from "@/components/admin/EntityContacts";
import { CheckboxField, NumberField, TextField } from "@/components/admin/fields";
import { toast } from "@/components/feedback/ToastHost";
import { EmptyState } from "@/components/feedback/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getMapsKey } from "@/services/api/settings";
import { createSite, deleteSite, listSites, updateSite } from "@/services/api/sites";
import type { Site } from "@/services/api/types";

interface SiteForm {
  site_code: string;
  name: string;
  address: string;
  postcode: string;
  timezone: string;
  is_depot: boolean;
  bays_count: string;
  color: string;
}

const emptyForm: SiteForm = {
  site_code: "",
  name: "",
  address: "",
  postcode: "",
  timezone: "Europe/London",
  is_depot: true,
  bays_count: "0",
  color: "",
};

/* ------------------------------------------------- sites map (all sites) */

/** Escape HTML in values interpolated into the marker info card. */
function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

/** HTML for a site marker's info card (theme-aware background). */
function siteCardHtml(site: Site): string {
  const address = [site.address, site.postcode].filter(Boolean).join(", ") || "—";
  const rows: Array<[string, string]> = [
    ["Code", site.site_code],
    ["Name", site.name],
    ["Address", address],
    ["Type", site.is_depot ? "Depot" : "Site"],
    ["Bays", String(site.bays_count)],
  ];
  const body = rows
    .map(
      ([key, value]) =>
        `<div style="display:flex;justify-content:space-between;gap:14px;padding:2px 0;">` +
        `<span style="color:var(--color-muted-foreground);">${esc(key)}</span>` +
        `<span style="font-weight:600;color:var(--color-card-foreground);">${esc(value)}</span></div>`,
    )
    .join("");
  return (
    `<div style="background:var(--color-card);color:var(--color-card-foreground);border:1px solid var(--color-border);border-radius:10px;padding:10px 12px;font-family:ui-sans-serif,system-ui,sans-serif;font-size:13px;min-width:230px;box-shadow:0 6px 16px rgba(0,0,0,0.18);">` +
    `<div style="font-weight:700;margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid var(--color-border);">${esc(site.site_code)}</div>` +
    body +
    `</div>`
  );
}

type SitesMapGoogle = {
  maps: {
    Map: new (el: HTMLElement, opts: unknown) => unknown;
    Geocoder: new () => {
      geocode: (
        request: { address: string },
        cb: (results: Array<{ geometry: { location: { lat(): number; lng(): number } } }>, status: string) => void,
      ) => void;
    };
    Marker: new (opts: {
      position: { lat: number; lng: number };
      map: unknown;
      title?: string;
      icon?: unknown;
    }) => unknown;
    InfoWindow: new (opts?: { content?: string }) => {
      setContent: (content: string) => void;
      open: (opts: { map?: unknown; anchor?: unknown }) => void;
      close: () => void;
    };
    event: {
      addListener: (target: unknown, eventName: string, handler: () => void) => unknown;
    };
    SymbolPath: { CIRCLE: number };
  };
};

/** Map panel plotting every site at its (validated) address. */
function SitesMap({ apiKey, sites }: { apiKey: string; sites: Site[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<unknown | null>(null);
  const markersRef = useRef<unknown[]>([]);
  const infoWindowRef = useRef<{
    setContent: (content: string) => void;
    open: (opts: { map?: unknown; anchor?: unknown }) => void;
    close: () => void;
  } | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [plotInfo, setPlotInfo] = useState<string | null>(null);

  // Load the Google Maps JS API once the key is known.
  useEffect(() => {
    if (!apiKey) {
      setStatus("error");
      return;
    }
    const win = window as unknown as { google?: SitesMapGoogle; __gmapsReady?: () => void };
    if (win.google?.maps) {
      setStatus("ready");
      return;
    }
    const existing = document.getElementById("gmaps-script");
    if (existing) {
      const onLoad = () => setStatus("ready");
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }
    setStatus("loading");
    win.__gmapsReady = () => setStatus("ready");
    const script = document.createElement("script");
    script.id = "gmaps-script";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=__gmapsReady&loading=async`;
    script.onerror = () => setStatus("error");
    document.head.appendChild(script);
    return () => {
      script.onerror = null;
    };
  }, [apiKey]);

  // Initialise the map once the API is ready.
  useEffect(() => {
    if (status !== "ready" || !containerRef.current || mapRef.current) return;
    const g = (window as unknown as { google: SitesMapGoogle }).google;
    mapRef.current = new g.maps.Map(containerRef.current, {
      center: { lat: 53.2, lng: -1.5 },
      zoom: 6,
    });
  }, [status]);

  // Plot a pin per site. Uses the site's SAVED lat/lng when present (set by
  // the backend when the address is validated); only missing ones are geocoded.
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    const g = (window as unknown as { google: SitesMapGoogle }).google;

    for (const marker of markersRef.current) {
      (marker as { setMap: (map: unknown) => void }).setMap(null);
    }
    markersRef.current = [];
    if (infoWindowRef.current) infoWindowRef.current.close();
    infoWindowRef.current = new g.maps.InfoWindow();
    setPlotInfo(null);

    const geocoder = new g.maps.Geocoder();
    const coords = new Map<number, { lat: number; lng: number }>();
    const toGeocode = new Map<string, number[]>(); // address -> site ids

    for (const site of sites) {
      if (site.latitude != null && site.longitude != null) {
        coords.set(site.id, { lat: site.latitude, lng: site.longitude });
      } else {
        const parts = [site.address, site.postcode].filter(Boolean).join(", ");
        if (parts) {
          const ids = toGeocode.get(parts) ?? [];
          ids.push(site.id);
          toGeocode.set(parts, ids);
        }
      }
    }

    let pending = toGeocode.size;
    let geocoded = 0;
    let failed = 0;

    const placeMarkers = () => {
      let placed = 0;
      for (const site of sites) {
        const coord = coords.get(site.id);
        if (!coord) continue;
        const marker = new g.maps.Marker({
          position: coord,
          map: mapRef.current,
          title: `${site.site_code} — ${site.name}`,
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: site.is_depot ? "#0f766e" : "#7c3aed",
            fillOpacity: 1,
            strokeColor: "#0f172a",
            strokeWeight: 2,
          },
        });
        markersRef.current.push(marker);
        placed += 1;
        g.maps.event.addListener(marker, "click", () => {
          infoWindowRef.current?.setContent(siteCardHtml(site));
          infoWindowRef.current?.open({ map: mapRef.current, anchor: marker });
        });
      }
      // Centre the map on the plotted sites.
      if (coords.size > 0) {
        const values = [...coords.values()];
        const lat = values.reduce((sum, c) => sum + c.lat, 0) / values.length;
        const lng = values.reduce((sum, c) => sum + c.lng, 0) / values.length;
        const spread =
          Math.max(...values.map((c) => c.lat)) - Math.min(...values.map((c) => c.lat));
        const zoom = spread > 2 ? 6 : spread > 0.5 ? 7 : 8;
        (mapRef.current as { setCenter: (c: { lat: number; lng: number }) => void }).setCenter({
          lat,
          lng,
        });
        (mapRef.current as { setZoom: (z: number) => void }).setZoom(zoom);
      }
      setPlotInfo(
        placed === 0
          ? toGeocode.size > 0
            ? "No pins could be placed — enable the Google Geocoding API for the Maps API key, then re-save site addresses."
            : "No sites with addresses to plot."
          : `${placed} site${placed === 1 ? "" : "s"} plotted (${coords.size - geocoded} saved coords, ${geocoded} geocoded${failed > 0 ? `, ${failed} failed` : ""}).`,
      );
    };

    toGeocode.forEach((ids, address) => {
      geocoder.geocode({ address }, (results, statusCode) => {
        pending -= 1;
        if (statusCode === "OK" && results[0]?.geometry?.location) {
          const location = results[0].geometry.location;
          const coord = { lat: location.lat(), lng: location.lng() };
          for (const id of ids) coords.set(id, coord);
          geocoded += 1;
        } else {
          failed += 1;
        }
        if (pending === 0) placeMarkers();
      });
    });
    if (toGeocode.size === 0) placeMarkers();
  }, [status, sites]);

  return (
    <div className="relative min-h-[360px] overflow-hidden rounded-xl border bg-muted/20">
      <div ref={containerRef} className="h-[360px] w-full" />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 text-sm text-muted-foreground">
          Loading Google Maps…
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <EmptyState
            title="Map unavailable"
            description="Add a Google Maps API key on the Settings page to plot site locations."
          />
        </div>
      )}
      {status === "ready" && plotInfo && (
        <div className="absolute inset-x-2 bottom-2 z-10 rounded-md bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow">
          {plotInfo}
        </div>
      )}
    </div>
  );
}

export function SitesPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const query = useQuery({
    queryKey: ["sites", offset],
    queryFn: () => listSites({ limit, offset }),
  });
  const allSites = useQuery({
    queryKey: ["sites", "all"],
    queryFn: () => listSites({ limit: 500 }),
  });
  const mapsKey = useQuery({
    queryKey: ["maps-key"],
    queryFn: () => getMapsKey(),
  });

  const columns: ColumnDef<Site>[] = [
    { accessorKey: "site_code", header: t("admin.siteCode") },
    { accessorKey: "name", header: t("admin.name") },
    { accessorKey: "bays_count", header: t("admin.baysCount") },
    { accessorKey: "timezone", header: t("admin.timezone") },
    {
      accessorKey: "is_depot",
      header: t("admin.isDepot"),
      cell: ({ getValue }) => (getValue() ? t("common.yes") : t("common.no")),
    },
    {
      accessorKey: "color",
      header: t("admin.siteColor"),
      cell: ({ getValue }) => {
        const color = getValue() as string | null;
        return color ? (
          <span
            className="inline-block h-4 w-4 rounded-full border"
            style={{ backgroundColor: color }}
            title={color}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Site locations</h2>
      </div>
      <SitesMap apiKey={mapsKey.data?.api_key ?? ""} sites={allSites.data?.items ?? []} />
      <FullCrudPage<Site, SiteForm>
      title={t("nav.sites")}
      entityType="site"
      columns={columns}
      data={query.data?.items ?? []}
      total={query.data?.total ?? 0}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="sites"
      emptyForm={emptyForm}
      toForm={(r) => ({
        site_code: r.site_code,
        name: r.name,
        address: r.address ?? "",
        postcode: r.postcode ?? "",
        timezone: r.timezone,
        is_depot: r.is_depot,
        bays_count: r.bays_count.toString(),
        color: r.color ?? "",
      })}
      canSubmit={(f) => Boolean(f.site_code && f.name)}
      renderForm={(form, setForm) => (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("admin.siteCode")}
            value={form.site_code}
            onChange={(v) => setForm({ ...form, site_code: v })}
            required
          />
          <TextField
            label={t("admin.name")}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <TextField
            label={t("admin.address")}
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
          />
          <TextField
            label={t("admin.postcode")}
            value={form.postcode}
            onChange={(v) => setForm({ ...form, postcode: v })}
          />
          <TextField
            label={t("admin.timezone")}
            value={form.timezone}
            onChange={(v) => setForm({ ...form, timezone: v })}
          />
          <NumberField
            label={t("admin.baysCount")}
            value={form.bays_count}
            onChange={(v) => setForm({ ...form, bays_count: v })}
          />
          <CheckboxField
            label={t("admin.isDepot")}
            checked={form.is_depot}
            onChange={(v) => setForm({ ...form, is_depot: v })}
          />
          <div className="col-span-2 space-y-2">
            <Label>{t("admin.siteColor")}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                value={form.color || "#cccccc"}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-10 w-16 shrink-0 cursor-pointer"
                aria-label={t("admin.siteColor")}
              />
              <Input
                type="text"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#RRGGBB"
                className="font-mono"
                aria-label={t("admin.siteColor")}
              />
            </div>
            <p className="text-xs text-muted-foreground">{t("admin.siteColorHint")}</p>
          </div>
        </div>
      )}
      onSave={async (mode, form, record) => {
        const payload = {
          site_code: form.site_code,
          name: form.name,
          address: form.address || null,
          postcode: form.postcode || null,
          timezone: form.timezone,
          is_depot: form.is_depot,
          bays_count: form.bays_count ? Number(form.bays_count) : 0,
          color: form.color.trim() || null,
        };
        const saved =
          mode === "edit" && record ? await updateSite(record.id, payload) : await createSite(payload);
        // Surface the Google Maps address-validation result.
        if (saved.geocode_status === "failed") {
          toast.error(
            "Address could not be validated — enable the Google Geocoding API for the Maps API key, then re-save.",
          );
        } else if (saved.geocode_status === "ok" && saved.latitude != null) {
          toast.success("Address validated — location saved for the site map.");
        }
        return saved;
      }}
      onDelete={(record) => deleteSite(record.id)}
      recordTitle={(r) => r.site_code}
      detailFields={(r) => [
        { label: t("admin.siteCode"), value: r.site_code, field: "site_code" },
        { label: t("admin.name"), value: r.name, field: "name" },
        { label: t("admin.address"), value: r.address, field: "address" },
        { label: t("admin.postcode"), value: r.postcode, field: "postcode" },
        {
          label: "Location",
          value:
            r.latitude != null && r.longitude != null
              ? `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`
              : r.geocode_status === "failed"
                ? "Address not validated"
                : "—",
        },
        { label: t("admin.timezone"), value: r.timezone, field: "timezone" },
        { label: t("admin.baysCount"), value: r.bays_count, field: "bays_count" },
        {
          label: t("admin.isDepot"),
          value: r.is_depot ? t("common.yes") : t("common.no"),
          field: "is_depot",
        },
        { label: t("admin.siteColor"), value: r.color ? r.color : "—", field: "color" },
      ]}
      viewExtras={(r) => <EntityContacts ownerType="site" ownerId={r.id} readOnly />}
      editExtras={(r) => <EntityContacts ownerType="site" ownerId={r ? r.id : null} />}
      />
    </div>
  );
}
